import currency from 'currency.js'
import moment from 'moment'
import SoundWon from '/assets/sounds/won.mp3'
import Deal from '~/models/deal'
import Note from '~/models/note'
import SMS from '~/models/sms'
import Email from '~/models/email'
import Upload from '~/models/upload'
import Task from '~/models/task'
import Lead from '~/models/lead'
import Appointment from '~/models/appointment'
import CallModel from '~/models/call'
import humanizeDuration from 'humanize-duration'
import type moment from 'moment'

interface State {
    name: string
    timer: any
    dp: Function
    dpToFloat: Function
    cleanNumber: Function
    e164ToNumber: Function
    capitalizeFirstLetter: Function
    getComponent: Function
    getParentUntil: Function
    delay: Function
    globalSubmitDelay: Function
    fmtMSS: Function
    chunk: Function
    randomColor: Function
    sleep: Function
    idName: Function
    pluralize: Function
    stripeCardError: Function
    lostColour: Function
    formatDateTime: Function
    getNestedArrayValue: Function
    check: Function
    generatePassword: Function
    getTotalOffsetParent: Function
    confettiTime: Function
    modelIdToName: Function
    empty: Function
    default: Function
    won: Function
    tenth: Function
    explode: Function
    due: Function
    since: Function
    adminUsers: Function
}

const help: State = {
    name: 'Helpers',
    timer: null,

    dp(a: number = 0, precision: number = 2, symbol: any = '£') {
        if (isNaN(a) || isFinite(a) == false) {
            a = 0
        }

        return currency(a, {
            precision: precision,
            separator: ',',
            symbol: symbol !== false ? symbol : '',
        }).format() // false
    },

    dpToFloat(a: any) {
        return Number(String(a).replace(/[^0-9.-]+/g, ''))
    },

    tenth(a: number = 0) {
        let after = '';
        let value: string | number = a;

        if (a >= 1_000_000) {
            value = (a / 1_000_000).toFixed(0);
            after = 'm';
        } else if (a >= 1_000) {
            value = (a / 1_000).toFixed(0);
            after = 'k';
        } else {
            value = a.toFixed(0);
        }

        return '£' + value + after;
    },

    cleanNumber(n: any) {
        if (n == null) {
            return ''
        }
        return n.replace('+44', '0')
    },

    e164ToNumber(n: any, spaces: boolean = true, brackets: boolean = true) {
        if (n == null) {
            return null
        }
        if (n == '') {
            return ''
        }

        if (n == '+266696687') {
            return 'Withheld number'
        }

        n = n.replace('+44', '0').replace(/ /g, '')

        /*
        020,023,024,028,029 - (XXX) XXXX XXXX
        0113,0114,0115,0116,0117,0118,0121,0131,0141,0151,0161,0191 - (XXXX) XXX XXXX
        Anything else - (XXXXX) XXX XXX
        */
        let tests = {
            major: {
                numbers: ['020', '023', '024', '028', '029'],
                format: [0, 3, 3, 4, 7, 4],
            },
            minor: {
                numbers: ['0113', '0114', '0115', '0116', '0117', '0118', '0121', '0131', '0141', '0151', '0161', '0191'],
                format: [0, 4, 4, 3, 7, 4],
            },
            default: {
                numbers: [],
                format: [0, 5, 5, 3, 8, 3],
            },
        }

        let format: any = null

        for (let i in tests) {
            for (let s = 0; s < tests[i].numbers.length; s++) {
                if (n.indexOf(tests[i].numbers[s]) === 0) {
                    format = tests[i]
                    break
                }

                if (format != null) {
                    break
                }
            }
        }

        if (format == null) {
            format = tests.default
        }

        // console.group()
        // console.log(n)
        // console.log(n.substr(format.format[0], format.format[1]))
        // console.log(n.substr(format.format[2], format.format[1]))
        // console.log(n.substr(format.format[4], format.format[5]))
        // console.groupEnd()

        let a =
            (brackets ? '(' : '') +
            n.substr(format.format[0], format.format[1]) +
            (brackets ? ')' : '') +
            ' ' +
            n.substr(format.format[2], format.format[3]) + ' ' +
            n.substr(format.format[4], format.format[5])

        if (spaces == false) {
            a = a.replace(/ /g, '')
        }

        return a
    },

    capitalizeFirstLetter(string: string) {
        if (string == null || string == '' || string.length <= 0) {
            return ''
        }

        

        return String(string).charAt(0).toUpperCase() + String(string).slice(1).toLowerCase()
    },

    getComponent(a: any, name: string) {
        let component = null
        let parent = a.$parent

        while (parent && !component) {
            if (parent.$options.name === name) {
                component = parent
            }
            parent = parent.$parent
        }

        return component
    },

    getParentUntil(parent: any, a: any) {
        let node = a.parentNode

        while (node != null) {
            // console.log(node.dataset);
            if (node.dataset.ignoreClose !== undefined) {
                return false
            }

            if (node == parent) {
                return true
            }

            node = node.parentNode
        }

        return true
    },

    delay(callback: Function, delay: number = 600) {
        if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
        }

        this.timer = setTimeout(() => {
            if (callback) {
                callback()
            }
        }, delay)
    },

    globalSubmitDelay() {
        return 800
    },

    fmtMSS(a: string) {
        if (a == null) {
            return '0:00'
        }

        let b: number = parseInt(a)

        return (b - (b %= 60)) / 60 + (9 < b ? ':' : ':0') + b
    },

    chunk(array: [], chunkSize: number) {
        let newArray: object[][] = []

        for (let i = 0; i < array.length; i += chunkSize) {
            newArray.push(array.slice(i, i + chunkSize))
        }

        return newArray
    },

    randomColor(type: string = 'bg', index = null) {
        let colors = [type + '-red-400', type + '-blue-400', type + '-green-400', type + '-purple-400']

        if (index != null) {
            return colors[index]
        }

        return colors[Math.floor(Math.random() * colors.length)]
    },

    sleep(milliseconds: number) {
        const date: number = Date.now()
        let currentDate: number = 0

        do {
            currentDate = Date.now()
        } while (currentDate - date < milliseconds)
    },

    idName(id: any) {
        let n: string = ''

        switch (parseInt(id)) {
            case 102:
                n = 'contact'
                break
            case 103:
                n = 'deal'
                break
        }

        return n
    },

    pluralize(word: string, count: any = 0, suffix: string = 's') {
        if (parseInt(count) !== 1 && word[word.length - 1] == suffix) {
            return word
        }

        return `${word}${parseInt(count) !== 1 ? suffix : ''}`
    },

    stripeCardError(error: { decline_code: string; message: string; code: string }) {
        switch (error.decline_code) {
            case 'generic_decline':
            case 'lost_card':
            case 'stolen_card':
                return 'Your card has been declined. Please try again or use another card.'
                break
            case 'insufficient_funds':
                return 'Your card has insufficient funds. Please use another card.'
                break
        }

        switch (error.code) {
            case 'expired_card':
                return 'Your card has expired. Please use another card.'
                break
            case 'incorrect_cvc':
                return "Your card's security code (CVC) is incorrect."
                break
            case 'processing_error':
                return 'An error occurred while processing your payment. Please try again shortly.'
                break
            case 'invalid_number':
            case 'incorrect_number':
                return 'Your card number is invalid. Check the number and try again.'
                break
        }

        return error.message
    },

    lostColour(reason: string, css = true) {
        if (css) {
            switch (reason) {
                case 'Lost to competition':
                    return 'bg-blue-500'
                    break
                case 'We were too expensive':
                    return 'bg-purple-500'
                    break
                case 'Poor follow up':
                    return 'bg-red-600'
                    break
                case 'Problem customer':
                    return 'bg-red-700'
                    break
                case 'We didnt want the work':
                    return 'bg-green-500'
                    break
                case 'Unable to do the work':
                    return 'bg-green-700'
                    break
                case 'Service not offered':
                    return 'bg-red-500'
                    break
                case 'Timing issue':
                    return 'bg-blue-700'
                    break
                case 'Other':
                    return 'bg-grey-700'
                    break
            }
        }

        switch (reason) {
            case 'Lost to competition':
                return '#6BF0FF'
                break
            case 'We were too expensive':
                return '#8B4DB6'
                break
            case 'Poor follow up':
                return '#FF9026'
                break
            case 'Problem customer':
                return '#DC583C'
                break
            case 'We didnt want the work':
                return '#00B885'
                break
            case 'Unable to do the work':
                return '#14634F'
                break
            case 'Service not offered':
                return '#FFCD40'
                break
            case 'Timing issue':
                return '#0077B5'
                break
            case 'Other':
                return '#C9C9C9'
                break
        }
    },

    formatDateTime(
        dt: string,
        params: {
            date?: boolean
            time?: boolean
            space?: string
            dateFormat?: string
        },
    ) {
        let dateTime: any = moment(dt)
        let options: any = {
            date: params?.date != undefined ? params.date : true,
            time: params?.time != undefined ? params.time : true,
            // space: params.space != undefined ? params.space : ' [@] ',
            space: params?.space != undefined ? params.space : ' ',
            dateFormat: params?.dateFormat != undefined ? params.dateFormat : 'DD/MM/YYYY',
        }

        let format: string = ''
        format += options.date ? options.dateFormat : ''
        format += options.date && options.time ? options.space : ''
        // format += (options.time ? (dateTime.minutes() > 0 ? 'hh:mma' : 'ha') : '');
        format += options.time ? 'HH:mm' : ''

        if (dateTime.format(format).toLowerCase() == 'invalid date') {
            return null
        }

        return dateTime.format(format)
    },

    getNestedArrayValue(obj: object, str: string) {
        const keys = str.split('.')
        let value = obj

        for (let i = 0; i < keys.length; i++) {
            value = value[keys[i]]
        }

        return value
    },

    check(user: any, key: string) {
        let check: any = this.getNestedArrayValue(user.labels, key)

        return check == null || check == true
    },

    generatePassword() {
        let charactersArray: any = ['a-z', 'A-Z', '0-9', '#']
        let CharacterSet: string = ''
        let password: string = ''

        if (charactersArray.indexOf('a-z') >= 0) {
            CharacterSet += 'abcdefghijklmnopqrstuvwxyz'
        }
        if (charactersArray.indexOf('A-Z') >= 0) {
            CharacterSet += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        }
        if (charactersArray.indexOf('0-9') >= 0) {
            CharacterSet += '0123456789'
        }
        if (charactersArray.indexOf('#') >= 0) {
            // CharacterSet += '![]{}()%&*$#^<>~@|';
            CharacterSet += '!%&*$#^@'
        }

        for (let i: number = 0; i < 10; i++) {
            password += CharacterSet.charAt(Math.floor(Math.random() * CharacterSet.length))
        }

        return password
    },

    getTotalOffsetParent(element: any) {
        let totalOffsetParent: any = element.offsetTop + 200

        while (element.offsetParent) {
            totalOffsetParent += element.offsetParent.offsetTop
            element = element.offsetParent
        }

        return totalOffsetParent
    },

    confettiTime(vue: any) {
        let particles = []

        for (let i = 1; i <= 8; i++) {
            particles.push({
                type: 'image',
                size: 6,
                url: '/confetti/tl-0-' + i + '.svg',
            })

            particles.push({
                type: 'image',
                size: 6,
                url: '/confetti/tr-0-' + i + '.svg',
            })

            particles.push({
                type: 'image',
                size: 6,
                url: '/confetti/tb-0-' + i + '.svg',
            })
        }

        if (this.confetti == null) {
            vue.$confetti.start({
                particlesPerFrame: 1,
                particles: particles,
            })
        }

        clearInterval(this.confetti)

        this.confetti = setTimeout(() => {
            vue.$confetti.stop()
            this.confetti = null
        }, 7000)
    },

    won(vue: any) {
        if (this.soundEffect == null) {
            this.soundEffect = new Audio(SoundWon)
            this.soundEffect.load()
            this.soundEffect.volume = 0.6
        }

        this.soundEffect.play()

        if (this.soundEffect.currentTime > 0) {
            this.soundEffect.currentTime = 0
        }

        this.confettiTime(vue)
    },

    modelIdToName(id: any) {
        id = parseInt(id)

        return {
            // 46: Setting,
            // 48: Communication,
            100: Lead,
            // 102: Contact,
            103: Deal,
            201: SMS,
            202: Note,
            203: Email,
            204: Upload,
            205: Appointment,
            206: CallModel,
            207: Task,
        }[id];
    },

    empty(value: any) {
        return value == null || value == undefined || value == '' ? true : false
    },

    default(value: any, defaultValue: any = null) {
        return this.empty(value) ? defaultValue : value
    },

    explode(str: string, delimiter: string = ',', part: any = null) {
        let a = str.split(delimiter)

        if (part != null) {
            if (part == -1) {
                return a[a.length - 1]
            }

            return a[part]
        }

        return a
    },

    due(date: string, largest: number = 2) {
        let diff = moment(date).diff(moment())
        let after = (diff < 0) ? ' ago' : ''

        return (
            humanizeDuration(diff, {
                largest: largest,
                round: true,
                units: ['y', 'mo', 'w', 'd', 'h', 'm'],
            }) + after
        )
    },

    since(date: string, largest: number = 2) {
        let diff = moment().diff(moment(date))

        return (
            humanizeDuration(diff, {
                largest: largest,
                round: true,
                units: ['y', 'mo', 'w', 'd', 'h', 'm'],
            })
        )
    },

    adminUsers() {
        return [
            { "value": 1, "html": "James Waring" },
            { "value": 2, "html": "Nick Cole" },
            { "value": 3, "html": "Leon Cullen" },
            { "value": 4, "html": "Holly Carlo" },
            { "value": 5, "html": "Natasha Higgins" },
            { "value": 11317, "html": "Antonia Mougkopetrou" },
            // { "value": 14697, "html": "Patrick Lewis" },
            // { "value": 33817, "html": "Jo McConnell" },
            { "value": 38250, "html": "Conor McVeagh" },
            // { "value": 38251, "html": "Sean Stewart" },
            { "value": 47270, "html": "Chris Mallon" },
            { "value": 47271, "html": "Josh Young" },
            { "value": 47686, "html": "Stephanie Monkhouse" }
        ]
    }
}

export default defineNuxtPlugin((nuxtApp) => {
    return {
        provide: {
            help: help,
        },
    }
})
